import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { FrameViews } from "@com.mgmtp.a12.client/client-core/lib/core/frame";
import { ViewSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { ModalOverlay } from "@com.mgmtp.a12.widgets/widgets-core/lib/modal-overlay";

interface Constraints {
	readonly fullscreen?: unknown;
	readonly fitToParent?: unknown;
	readonly wideModal?: unknown;
}

const UNAFFECTED_MODALS = [
	"Loeschen",
	"Bestaetigen",
	"DeleteProcessConfirmModal",
	"ChangeMembershipModal",
	"EldoradoCancelModal",
	"EldoradoArchival",
	"CustomContactTreeEngine",
	"CustomTextbausteineTreeEngine"
];
const STACK_VIEW_DESCRIPTOR_KEY = "stack_view";

export const CustomModalRegionUi: React.ComponentType<FrameViews.RegionUiProps> = function CustomModalRegionUi(props) {
	const dispatch = useDispatch();

	const { layout, views } = useSelector(ViewSelectors.region(props.regionReference));
	const Layout = props.layoutProvider(layout.name);

	const activityIds = views.map(v => v.activityId);

	const onClose = React.useCallback(() => {
		dispatch(ActivityActions.cancelRequested({ activityIds }));
	}, [dispatch, activityIds]);

	const fullscreen = views.some(({ constraints }: { constraints?: Constraints }) => constraints?.fullscreen === true);
	const fitToParent = views.some(
		({ constraints }: { constraints?: Constraints }) => constraints?.fitToParent === true
	);
	const activities = useSelector(ActivitySelectors.activities());
	const stackedView = [...views].reverse().find(view => hasStackViewFlag(activities[view.activityId]));
	const baseView = stackedView
		? [...views].reverse().find(view => view.activityId !== stackedView.activityId)
		: undefined;

	if (stackedView && baseView) {
		const StackedViewComponent = props.viewProvider(stackedView.name);
		const baseViews = [baseView];
		const baseActivityIds = baseViews.map(v => v.activityId);
		const onCloseBase = () => dispatch(ActivityActions.cancelRequested({ activityIds: baseActivityIds }));
		return (
			<>
				<ModalOverlay
					closeOnEsc
					closeOnOutsideClick
					onClose={onCloseBase}
					fullscreen={fullscreen}
					fitToParent={fitToParent}
				>
					<StyledWrapper fullscreen={fullscreen}>
						<Layout.component {...props} settings={layout.settings} views={baseViews} />
					</StyledWrapper>
				</ModalOverlay>
				<StackedViewComponent key={stackedView.activityId} {...stackedView} ariaLevel={2} />
			</>
		);
	}
	if (stackedView) {
		const StackedViewComponent = props.viewProvider(stackedView.name);
		return <StackedViewComponent key={stackedView.activityId} {...stackedView} ariaLevel={1} />;
	}

	const unaffectedView = [...views].reverse().find(view => {
		const activity = activities[view.activityId];
		const modal = activities[view.activityId]?.descriptor.modal;
		return modal !== undefined && !hasStackViewFlag(activity) && UNAFFECTED_MODALS.includes(modal);
	});

	if (unaffectedView) {
		const ViewComponent = props.viewProvider(unaffectedView.name);
		return <ViewComponent key={unaffectedView.activityId} {...unaffectedView} ariaLevel={1} />;
	}

	return views.length > 0 ? (
		<ModalOverlay
			closeOnEsc
			closeOnOutsideClick
			onClose={onClose}
			fullscreen={fullscreen}
			fitToParent={fitToParent}
		>
			<StyledWrapper fullscreen={fullscreen}>
				<Layout.component {...props} settings={layout.settings} views={views} />
			</StyledWrapper>
		</ModalOverlay>
	) : null;
};

const StyledWrapper = styled.div<{ readonly fullscreen: boolean }>`
	border-radius: ${({ theme }) => theme.components.contentBox.contentBoxBorderRadius};

	${({ fullscreen }) =>
		fullscreen
			? css`
					width: 100%;
					height: 100%;
				`
			: ""}
`;

function hasStackViewFlag(activity: unknown): boolean {
	const flag =
		typeof activity === "object" && activity !== null
			? (activity as { descriptor?: Record<string, unknown> }).descriptor?.[STACK_VIEW_DESCRIPTOR_KEY]
			: undefined;
	return flag === true || flag === "true";
}
